import { hashKey, useQueryClient, type QueryKey } from "@tanstack/react-query"
import { useCallback, useRef, useSyncExternalStore } from "react"

/**
 * Liest einen Cache-Eintrag MIT, ohne ihn zu besitzen.
 *
 * 🔴 Anlass 02.09.2026: „Fehler: Missing queryFn: '["email","7299"]'" stand rot
 * in der Inbox, die Mail liess sich gar nicht mehr oeffnen.
 *
 * Der Hergang: zwei Komponenten lesen denselben Schluessel. Eine holt die
 * Daten wirklich (`EmailDetailPane`), die andere wollte nur mitlesen und nahm
 * dafuer `queryFn: skipToken`. In React Query gehoert die `queryFn` aber nicht
 * dem Observer, sondern dem **Query** — wer zuletzt seine Optionen setzt,
 * gewinnt. Montiert der Mitleser nach dem Eigentuemer, steht am Query
 * `skipToken`, und beim naechsten Nachladen (nach `invalidateQueries`, bei
 * Fenster-Fokus) hat die Abfrage keine Funktion mehr. Der Fehler trifft dann
 * ausgerechnet den Eigentuemer.
 *
 * Davor stand eine Dummy-`queryFn` (`async () => undefined`) an derselben
 * Stelle — die schrieb ihr leeres Ergebnis ueber die echten Daten. Beide
 * Versuche scheitern an derselben Ursache: **ein Mitleser darf die Abfrage
 * gar nicht mitdefinieren.**
 *
 * Dieser Haken meldet deshalb keinen Observer an. Er haengt sich an den
 * Cache und liest den Wert; er laedt nie, er schreibt nie, und er kann dem
 * Eigentuemer nichts wegnehmen. Kein Wert im Cache heisst `undefined` — der
 * Mitleser wartet, bis der Eigentuemer geladen hat.
 */
export function useCacheMitleser<T>(key: QueryKey): T | undefined {
  const qc = useQueryClient()
  // Der Schluessel kommt als frisches Array je Render. Verglichen wird sein
  // Hash, benutzt wird der jeweils aktuelle Wert.
  const keyRef = useRef(key)
  keyRef.current = key
  const schluesselHash = hashKey(key)

  const anmelden = useCallback(
    (melde: () => void) => qc.getQueryCache().subscribe(melde),
    [qc],
  )
  // `getQueryData` gibt dieselbe Referenz zurueck, solange sich die Daten
  // nicht aendern — Pflicht fuer useSyncExternalStore, sonst rendert es
  // endlos.
  const lesen = useCallback(
    () => qc.getQueryData<T>(keyRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, schluesselHash],
  )
  return useSyncExternalStore(anmelden, lesen, lesen)
}
